import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MicIcon, SquareIcon, DownloadIcon, MessageSquareIcon, CheckCircle2Icon, ListOrderedIcon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
import { useLanguage } from '../components/LanguageContext';
import { AssociateStatusBubble } from '../components/AssociateStatusBubble';
import { AssociateButton } from '../components/AssociateButton';
type InstructionsPayload = {
  title: string;
  toolsNeeded: string[];
  steps: string[];
};
type Message = {
  role: 'user' | 'ai';
  type?: 'text';
  text: string;
} | {
  role: 'ai';
  type: 'instructions';
  payload: InstructionsPayload;
};
const intentSeeds: Record<string, Message[]> = {
  'shopping-list': [{
    role: 'ai',
    text: "Tell me about your project and I'll build you a shopping list of everything you need."
  }],
  'diy-instructions': [{
    role: 'ai',
    text: "Tell me about your project and I'll provide instructions on how to do it yourself."
  }],
  'contractor-quote': [{
    role: 'ai',
    text: 'Tell me about the project you want the contractor to quote.'
  }],
  default: [{
    role: 'user',
    text: 'I need to build a 12x16 deck.'
  }, {
    role: 'ai',
    text: 'Great. Will the deck be attached to your home?'
  }]
};
const demoInstructions: InstructionsPayload = {
  title: 'How to Replace a Bathroom Faucet',
  toolsNeeded: ['Adjustable wrench', 'Basin wrench', "Plumber's putty", 'Bucket', 'Towel'],
  steps: ['Turn off the water supply valves under the sink and open the faucet to drain remaining water.', 'Disconnect the supply lines from the existing faucet using an adjustable wrench.', 'Loosen the mounting nuts under the sink with a basin wrench and lift the old faucet out.', "Clean the sink surface and apply a thin bead of plumber's putty around the new faucet base.", 'Position the new faucet, then tighten mounting nuts from below until snug.', 'Reconnect the supply lines, turn water back on, and check for leaks.']
};
export function Chat() {
  useScreenInit();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    t
  } = useLanguage();
  const intent = (location.state as {
    intent?: string;
  } | null)?.intent;
  const isShoppingListIntent = intent === 'shopping-list';
  const isDiyIntent = intent === 'diy-instructions';
  const isContractorIntent = intent === 'contractor-quote';
  const isGeneralIntent = intent === 'general-help';
  const [isRecording, setIsRecording] = useState(false);
  const greetingKey = isShoppingListIntent ? 'chat.greeting.shoppingList' : isDiyIntent ? 'chat.greeting.diy' : isContractorIntent ? 'chat.greeting.contractor' : isGeneralIntent ? 'chat.greeting.general' : 'chat.greeting.default';
  const [messages, setMessages] = useState<Message[]>(isShoppingListIntent || isDiyIntent || isContractorIntent || isGeneralIntent ? [{
    role: 'ai',
    text: t(greetingKey)
  }] : intentSeeds.default);
  const handleMicTap = () => {
    if (isRecording) {
      setIsRecording(false);
      if (isShoppingListIntent) {
        setMessages(prev => [...prev, {
          role: 'user',
          text: "I'm building a 12x16 deck attached to my back door."
        }]);
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'ai',
            text: "Perfect. I'm putting together your shopping list now — give me one second..."
          }]);
          setTimeout(() => navigate('/list'), 1800);
        }, 1000);
      } else if (isDiyIntent) {
        setMessages(prev => [...prev, {
          role: 'user',
          text: 'I want to replace the faucet in my bathroom sink.'
        }]);
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'ai',
            text: "Great. Here's a step-by-step guide tailored to that project."
          }]);
          setTimeout(() => {
            setMessages(prev => [...prev, {
              role: 'ai',
              type: 'instructions',
              payload: demoInstructions
            }]);
          }, 900);
        }, 900);
      } else if (isContractorIntent) {
        setMessages(prev => [...prev, {
          role: 'user',
          text: 'I want to add a 12x16 deck to the back of my house.'
        }]);
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'ai',
            text: "Got it. I'll send your project details to vetted local contractors. You'll receive quotes within 24 hours."
          }]);
        }, 1000);
      } else {
        setMessages(prev => [...prev, {
          role: 'user',
          text: 'Yes, it will be attached to the back door.'
        }]);
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'ai',
            text: "Got it. I'm generating your complete shopping list now..."
          }]);
          setTimeout(() => navigate('/list'), 2000);
        }, 1000);
      }
    } else {
      setIsRecording(true);
    }
  };
  const headerLabel = isShoppingListIntent ? t('chat.header.shoppingList') : isDiyIntent ? t('chat.header.diy') : isContractorIntent ? t('chat.header.contractor') : isGeneralIntent ? t('chat.header.general') : t('chat.header.default');
  const headerStep = isShoppingListIntent || isDiyIntent || isContractorIntent || isGeneralIntent ? t('chat.step.describe') : 'Step 2 of 5';
  const progressWidth = isShoppingListIntent || isDiyIntent || isContractorIntent || isGeneralIntent ? 'w-1/5' : 'w-2/5';
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="flex flex-col h-full bg-white" data-id="element-81">
      <div className="px-6 py-4 border-b border-slate-100" data-id="element-82">
        <div className="flex items-center justify-between mb-2" data-id="element-83">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-84">
            {headerLabel}
          </span>
          <span className="text-xs font-medium text-blue-600" data-id="element-85">
            {headerStep}
          </span>
        </div>
        <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden" data-id="element-86">
          <div className={`bg-blue-600 h-full ${progressWidth} rounded-full`} data-id="element-87"></div>
        </div>
      </div>

      <AssociateStatusBubble data-id="element-88" />

      <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-89">
        {messages.map((msg, idx) => {
        if ((msg as any).type === 'instructions') {
          const payload = (msg as {
            payload: InstructionsPayload;
          }).payload;
          return <InstructionsCard key={idx} payload={payload} index={idx} data-id="element-90" />;
        }
        const m = msg as {
          role: 'user' | 'ai';
          text: string;
        };
        return <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: idx * 0.05
        }} key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-91">
              <div className={`max-w-[85%] rounded-2xl p-4 ${m.role === 'user' ? 'bg-blue-600 text-white rounded-tr-sm' : 'bg-slate-100 text-slate-900 rounded-tl-sm'}`} data-id="element-92">
                <p className="text-[15px] leading-relaxed" data-id="element-93">{m.text}</p>
              </div>
            </motion.div>;
      })}
        {isRecording && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="flex justify-end" data-id="element-94">
            <div className="max-w-[85%] rounded-2xl p-4 bg-blue-600 text-white rounded-tr-sm flex items-center gap-2" data-id="element-95">
              <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
            animationDelay: '0ms'
          }} data-id="element-96"></span>
              <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
            animationDelay: '150ms'
          }} data-id="element-97"></span>
              <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
            animationDelay: '300ms'
          }} data-id="element-98"></span>
            </div>
          </motion.div>}
      </div>

      <div className="px-6 pt-2 pb-6 flex flex-col items-center justify-center bg-gradient-to-t from-white via-white to-transparent" data-id="element-99">
        <p className="text-sm text-slate-500 mb-4 text-center h-5" data-id="element-100">
          {isRecording ? t('chat.listening') : t('chat.tapToSpeak')}
        </p>

        <div className="relative mb-5" data-id="element-101">
          {isRecording && <motion.div animate={{
          scale: [1, 1.5, 1],
          opacity: [0.5, 0, 0.5]
        }} transition={{
          repeat: Infinity,
          duration: 1.5
        }} className="absolute inset-0 bg-blue-400 rounded-full" data-id="element-102" />}
          <button onClick={handleMicTap} className={`relative z-10 w-20 h-20 rounded-full flex items-center justify-center shadow-lg transition-colors ${isRecording ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`} data-id="element-103">
            {isRecording ? <SquareIcon size={28} fill="currentColor" data-id="element-104" /> : <MicIcon size={32} data-id="element-105" />}
          </button>
        </div>

        <div className="w-full" data-id="element-106">
          <AssociateButton idleLabel={t('welcome.requestAssociate')} data-id="element-107" />
        </div>
      </div>
    </motion.div>;
}
function InstructionsCard({
  payload,
  index
}: {
  payload: InstructionsPayload;
  index: number;
}) {
  const [showTextSheet, setShowTextSheet] = useState(false);
  const [textSent, setTextSent] = useState(false);
  const [phone, setPhone] = useState('');
  const [downloaded, setDownloaded] = useState(false);
  const handleDownload = () => {
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 2200);
  };
  const handleSendText = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setTextSent(true);
    setTimeout(() => {
      setShowTextSheet(false);
      setTimeout(() => {
        setTextSent(false);
        setPhone('');
      }, 300);
    }, 1400);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.05
  }} className="flex justify-start" data-id="element-108">
      <div className="w-full max-w-[92%] rounded-2xl rounded-tl-sm bg-slate-100 overflow-hidden" data-id="element-109">
        <div className="bg-white m-1 rounded-xl border border-slate-200 overflow-hidden" data-id="element-110">
          <div className="px-4 pt-4 pb-3 border-b border-slate-100 flex items-start gap-3" data-id="element-111">
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0" data-id="element-112">
              <ListOrderedIcon size={16} data-id="element-113" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-114">
              <p className="text-xs font-medium text-blue-600 uppercase tracking-wider" data-id="element-115">
                DIY Instructions
              </p>
              <h3 className="text-[15px] font-semibold text-slate-900 leading-snug" data-id="element-116">
                {payload.title}
              </h3>
            </div>
          </div>

          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60" data-id="element-117">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-118">
              Tools needed
            </p>
            <div className="flex flex-wrap gap-1.5" data-id="element-119">
              {payload.toolsNeeded.map(tool => <span key={tool} className="text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-full px-2.5 py-1" data-id="element-120">
                  {tool}
                </span>)}
            </div>
          </div>

          <ol className="px-4 py-3 space-y-3" data-id="element-121">
            {payload.steps.map((step, i) => <li key={i} className="flex gap-3" data-id="element-122">
                <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5" data-id="element-123">
                  {i + 1}
                </span>
                <p className="text-[14px] text-slate-700 leading-relaxed flex-1" data-id="element-124">
                  {step}
                </p>
              </li>)}
          </ol>

          <div className="grid grid-cols-2 gap-2 p-3 border-t border-slate-100 bg-slate-50/40" data-id="element-125">
            <button onClick={handleDownload} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors" data-id="element-126">
              {downloaded ? <>
                  <CheckCircle2Icon size={16} data-id="element-127" />
                  Saved
                </> : <>
                  <DownloadIcon size={16} data-id="element-128" />
                  Download
                </>}
            </button>
            <button onClick={() => setShowTextSheet(true)} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white border-2 border-blue-100 hover:border-blue-200 text-blue-600 text-sm font-semibold transition-colors" data-id="element-129">
              <MessageSquareIcon size={16} data-id="element-130" />
              Text it
            </button>
          </div>
        </div>
      </div>

      {/* Text-it bottom sheet */}
      <AnimatePresence data-id="element-131">
        {showTextSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => !textSent && setShowTextSheet(false)} className="absolute inset-0 bg-slate-900/40 z-30" data-id="element-132" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 280
        }} className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 z-40 shadow-2xl" data-id="element-133">
              {textSent ? <div className="text-center py-6" data-id="element-134">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3" data-id="element-135">
                    <CheckCircle2Icon size={24} className="text-green-600" data-id="element-136" />
                  </div>
                  <p className="text-base font-semibold text-slate-900" data-id="element-137">
                    Instructions sent!
                  </p>
                  <p className="text-sm text-slate-500 mt-1" data-id="element-138">
                    Check your messages in a moment.
                  </p>
                </div> : <>
                  <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-5" data-id="element-139"></div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-140">
                    Text me the instructions
                  </h3>
                  <p className="text-sm text-slate-500 mb-4" data-id="element-141">
                    We'll send a link to the full guide.
                  </p>
                  <form onSubmit={handleSendText} className="space-y-3" data-id="element-142">
                    <input value={phone} onChange={e => setPhone(e.target.value)} type="tel" placeholder="(555) 555-5555" className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3.5 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100" data-id="element-143" />
                    <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-2xl flex items-center justify-center gap-2 transition-colors" data-id="element-144">
                      <MessageSquareIcon size={18} data-id="element-145" />
                      Send instructions
                    </button>
                    <button type="button" onClick={() => setShowTextSheet(false)} className="w-full text-slate-500 text-sm font-medium py-2" data-id="element-146">
                      Cancel
                    </button>
                  </form>
                </>}
            </motion.div>
          </>}
      </AnimatePresence>
    </motion.div>;
}